import { Badge, type BadgeProps } from '@/components/ui/Badge';
import {
  ANNUAL_OPTION_LABELS,
  SOFTWARE_STATUS_LABELS,
  renewalState,
  type AnnualOptionState,
} from '@/features/software/model';
import { formatCurrency } from '@/lib/formatters';
import type { SoftwareStatus } from '@/types/database';

type Variant = NonNullable<BadgeProps['variant']>;

const STATUS_VARIANT: Record<SoftwareStatus, Variant> = {
  active: 'success-soft',
  trial: 'info-soft',
  paused: 'muted',
  cancelled: 'muted',
};

export function SoftwareStatusBadge({ status }: { status: SoftwareStatus }) {
  return (
    <Badge variant={STATUS_VARIANT[status]} className="whitespace-nowrap">
      {SOFTWARE_STATUS_LABELS[status]}
    </Badge>
  );
}

const ANNUAL_VARIANT: Record<AnnualOptionState, Variant> = {
  already_annual: 'secondary',
  one_time: 'secondary',
  unknown: 'outline',
  unavailable: 'muted',
  available: 'info-soft',
  cheaper: 'success-soft',
  not_cheaper: 'muted',
};

export function AnnualOptionBadge({
  state,
  savingsCents,
}: {
  state: AnnualOptionState;
  savingsCents: number | null;
}) {
  const label =
    state === 'cheaper' && savingsCents != null
      ? `Save ${formatCurrency(savingsCents, { whole: true })}/yr`
      : ANNUAL_OPTION_LABELS[state];
  return (
    <Badge variant={ANNUAL_VARIANT[state]} className="whitespace-nowrap">
      {label}
    </Badge>
  );
}

export function RenewalBadge({
  renewsOn,
  status,
}: {
  renewsOn: string | null;
  status: SoftwareStatus;
}) {
  const state = renewalState({ renews_on: renewsOn, status });
  if (state === 'overdue') {
    return (
      <Badge variant="warning" className="whitespace-nowrap">
        Date passed
      </Badge>
    );
  }
  if (state === 'soon') {
    return (
      <Badge variant="warning-soft" className="whitespace-nowrap">
        Renews soon
      </Badge>
    );
  }
  return null;
}

export function NeedsPricingBadge() {
  return (
    <Badge variant="warning-soft" className="whitespace-nowrap">
      Needs pricing
    </Badge>
  );
}
